import {
  TextField,
  Box,
  Card,
  CardMedia,
  Typography,
  Button,
} from '@mui/material';
import React, { useState } from 'react';
import { blobUrl, uploadType } from '../Constants';
import WarningIcon from '@mui/icons-material/Warning';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import RadioButtonCheckedRoundedIcon from '@mui/icons-material/RadioButtonCheckedRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import YouTube from 'react-youtube';
import { Upload } from '../@types/api';

const opts: any = {
  height: '170',
  width: '100%',
  playerVars: {
    autoplay: 0,
    modestbranding: 1,
    showinfo: 0,
    rel: 0,
  },
};

interface Props {
  upload: Upload;
  isSelected: boolean;
  onUpdate: (upload: Upload) => void;
  isModal: boolean;
  onItemSelect: (upload: Upload) => void;
  onUseSelected: (upload: Upload) => void;
}
const FileCard: React.FC<Props> = ({
  upload,
  isSelected,
  onUpdate,
  isModal,
  onItemSelect,
  onUseSelected,
}) => {
  const [fileName, setFileName] = useState<string>(upload.fileName);
  const [altText, setAltText] = useState<string>(upload.altText);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const isAltTextValid = altText.length <= 50 && altText.length > 0;
  const isFileNameValid = fileName.length <= 50 && fileName.length > 0;

  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const updated = e.target.value;
    setFileName(updated);
    setIsDirty(true);
  };

  const handleAltTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const updated = e.target.value;
    setAltText(updated);
    setIsDirty(true);
  };

  const altTextLabel =
    upload.fileTypeId === uploadType.image ||
    upload.fileTypeId === uploadType.poster
      ? 'AltText:'
      : 'SongName:';
  const fileNameLabel =
    upload.fileTypeId === uploadType.youtube ? 'YouTubeRef:' : 'FileName:';

  const renderUpload = () => {
    switch (upload.fileTypeId) {
      case uploadType.image:
      case uploadType.poster:
        return (
          <>
            <CardMedia
              component='img'
              height='200'
              image={`${blobUrl}/images/${upload.fileName}`}
              alt={upload.altText}
              sx={{
                objectFit: 'cover',
                objectPosition: 'center top',
              }}
            />
          </>
        );
      case uploadType.audio:
        return (
          <Box
            p={1}
            style={{
              height: 100,
            }}
          >
            <Typography variant='subtitle2' gutterBottom>
              {upload.altText}
            </Typography>
            <audio controls>
              <source
                src={`${blobUrl}/mp3s/${upload.fileName}`}
                type='audio/mpeg'
              />
            </audio>
          </Box>
        );
      case uploadType.video:
      case uploadType.liveLoungeVideo:
        return (
          <Box
            pt={1}
            style={{
              backgroundColor: 'black',
              height: '170px',
              display: 'flex',
              alignItems: 'flex-end',
              position: 'relative',
            }}
          >
            <Typography
              position={'absolute'}
              color={'whitesmoke'}
              sx={{ top: 7, left: 10 }}
            >
              {upload.altText}
            </Typography>
            <video width='100%' controls>
              <source
                src={`${blobUrl}/videos/${upload.fileName}`}
                type='video/mp4'
              />
            </video>
          </Box>
        );
      case uploadType.youtube:
        return <YouTube videoId={upload.fileName} opts={opts} />;
      default:
        return null;
    }
  };

  const handleReset = () => {
    setAltText(upload.altText);
    setFileName(upload.fileName);
    setIsDirty(false);
  };

  const handleSave = () => {
    const updatedFile = { ...upload, fileName, altText };
    onUpdate(updatedFile);
    setIsDirty(false);
  };

  return (
    <Card
      sx={{ maxWidth: 345 }}
      raised={isSelected}
      variant={isSelected ? 'elevation' : 'outlined'}
    >
      {renderUpload()}
      {!isModal && (
        <>
          <Typography textAlign={'left'} mt={1} pl={1} color='primary'>
            Upload ID: {upload.uploadId}
          </Typography>
          <Box my={1} px={1}>
            <TextField
              fullWidth
              type='text'
              label={fileNameLabel}
              value={fileName}
              disabled={!isSelected}
              placeholder='Enter fileName'
              onChange={handleFileNameChange}
              size='small'
              sx={{ my: 1 }}
              error={!isFileNameValid}
              helperText={!isFileNameValid ? 'FileName cannot be empty' : ''}
            />
            <TextField
              fullWidth
              type='text'
              label={altTextLabel}
              value={altText}
              disabled={!isSelected}
              placeholder='Enter AltTag'
              onChange={handleAltTextChange}
              sx={{ my: 1 }}
              size='small'
              error={!isAltTextValid}
              helperText={!isAltTextValid ? 'AltText cannot be empty' : ''}
            />
          </Box>
          <Box
            justifyContent='space-between'
            display='flex'
            alignItems='center'
            sx={{ width: '100%' }}
          >
            <Button
              aria-label='cancel'
              onClick={handleReset}
              disabled={!isDirty}
              title='cancel'
              startIcon={
                <CancelIcon color={isDirty ? 'secondary' : 'disabled'} />
              }
            >
              Undo
            </Button>
            {isDirty && <WarningIcon color='warning' />}
            <Button
              aria-label='save'
              onClick={handleSave}
              title='save changes'
              disabled={!(isAltTextValid && isFileNameValid && isDirty)}
              startIcon={<SaveIcon color={isDirty ? 'primary' : 'disabled'} />}
            >
              Save
            </Button>
          </Box>
        </>
      )}

      <Box display={'flex'} justifyContent={'space-between'} p={1}>
        <Button
          variant='outlined'
          onClick={(e) => onItemSelect(upload)}
          startIcon={
            isSelected ? (
              <RadioButtonCheckedRoundedIcon color='secondary' />
            ) : (
              <RadioButtonUncheckedRoundedIcon color='secondary' />
            )
          }
        >
          Select
        </Button>
        {isModal && isSelected && (
          <Button
            variant='outlined'
            onClick={() => onUseSelected(upload)}
            size='small'
          >
            Use This
          </Button>
        )}
      </Box>
    </Card>
  );
};

export default FileCard;
